/**
 * Browser print for a consolidated invoice (#3933).
 *
 * Page 1 is one row per work order, the VAT breakdown and the single payment
 * reference. Each following page is that work order's lines, headed as an
 * attachment. A normal invoice never comes through here.
 */

import type { ConsolidatedRow } from './presentation';
import { summarizeVat, summarizeWorkOrders } from './presentation';

export interface ConsolidatedPrintLabels {
  title: string;
  period: string;
  registration: string;
  date: string;
  workOrder: string;
  reference: string;
  amount: string;
  vatSummary: string;
  vatRate: string;
  net: string;
  tax: string;
  gross: string;
  paymentReference: string;
  attachmentHeading: string;
  description: string;
  quantity: string;
  manualLines: string;
}

export interface ConsolidatedPrintSourceLine {
  bookingId: string | null;
  description: string;
  quantityLabel: string;
  amountLabel: string;
}

export interface ConsolidatedPrintDocument {
  title: string;
  invoiceNumber: string;
  periodLabel: string;
  paymentReferenceLabel: string;
  paymentReference: string;
  summaryHeaders: {
    registration: string;
    date: string;
    workOrder: string;
    reference: string;
    amount: string;
  };
  summaryRows: Array<{
    bookingId: string;
    registration: string;
    date: string;
    workOrder: string;
    reference: string;
    amount: string;
  }>;
  vatTitle: string;
  vatHeaders: { rate: string; net: string; tax: string; gross: string };
  vatRows: Array<{ rate: string; net: string; tax: string; gross: string }>;
  attachmentHeading: string;
  attachmentColumns: { description: string; quantity: string; amount: string };
  attachments: Array<{
    bookingId: string;
    title: string;
    lines: Array<{ description: string; quantity: string; amount: string }>;
  }>;
}

function dash(value: string | null): string {
  return value && value.trim() !== '' ? value : '—';
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

export function toConsolidatedPrintDocument(input: {
  invoiceNumber: string | null;
  paymentReferenceNumber: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  rows: readonly ConsolidatedRow[];
  lines: readonly ConsolidatedPrintSourceLine[];
  labels: ConsolidatedPrintLabels;
  formatMoney: (cents: number) => string;
  formatWhen: (value: string | null) => string;
  formatRate: (percent: number | null) => string;
}): ConsolidatedPrintDocument {
  const invoiceNumber = input.invoiceNumber && input.invoiceNumber.trim() !== ''
    ? input.invoiceNumber
    : '—';
  const summaries = summarizeWorkOrders(input.rows);
  const summaryRows = summaries.map((row) => ({
    bookingId: row.bookingId,
    registration: dash(row.registrationNumber),
    date: input.formatWhen(row.completedDate),
    workOrder: dash(row.workOrderNumber),
    reference: dash(row.customerReference),
    amount: input.formatMoney(row.grossCents),
  }));
  const vatRows = summarizeVat(input.rows).map((row) => ({
    rate: input.formatRate(row.taxPercent),
    net: input.formatMoney(row.netCents),
    tax: input.formatMoney(row.taxCents),
    gross: input.formatMoney(row.grossCents),
  }));
  const attachments = summaries.map((summary) => {
    const ownLines = input.lines.filter((line) => (line.bookingId ?? '') === summary.bookingId);
    const lines = ownLines.length > 0
      ? ownLines.map((line) => ({
          description: line.description,
          quantity: line.quantityLabel,
          amount: line.amountLabel,
        }))
      : input.rows
          .filter((row) => row.bookingId === summary.bookingId)
          .map((row) => ({
            description: input.formatRate(row.taxPercent),
            quantity: '',
            amount: input.formatMoney(row.grossCents),
          }));
    const title = summary.bookingId === ''
      ? input.labels.manualLines
      : [summary.workOrderNumber, summary.registrationNumber].filter((part) => part && part.trim() !== '').join(' · ');
    return {
      bookingId: summary.bookingId,
      title: title || input.labels.manualLines,
      lines,
    };
  });
  return {
    title: input.labels.title,
    invoiceNumber,
    periodLabel: `${input.labels.period} ${input.formatWhen(input.periodStart)}–${input.formatWhen(input.periodEnd)}`,
    paymentReferenceLabel: input.labels.paymentReference,
    paymentReference: dash(input.paymentReferenceNumber),
    summaryHeaders: {
      registration: input.labels.registration,
      date: input.labels.date,
      workOrder: input.labels.workOrder,
      reference: input.labels.reference,
      amount: input.labels.amount,
    },
    summaryRows,
    vatTitle: input.labels.vatSummary,
    vatHeaders: {
      rate: input.labels.vatRate,
      net: input.labels.net,
      tax: input.labels.tax,
      gross: input.labels.gross,
    },
    vatRows,
    attachmentHeading: input.labels.attachmentHeading,
    attachmentColumns: {
      description: input.labels.description,
      quantity: input.labels.quantity,
      amount: input.labels.amount,
    },
    attachments,
  };
}

function cells(values: string[], numericFrom: number): string {
  return values
    .map((value, index) => {
      const cls = index >= numericFrom ? ' class="num"' : '';
      return `<td${cls}>${escapeHtml(value)}</td>`;
    })
    .join('');
}

function headerCells(values: string[], numericFrom: number): string {
  return values
    .map((value, index) => {
      const cls = index >= numericFrom ? ' class="num"' : '';
      return `<th${cls}>${escapeHtml(value)}</th>`;
    })
    .join('');
}

export function buildConsolidatedInvoicePrintHtml(document: ConsolidatedPrintDocument): string {
  const summaryBody = document.summaryRows
    .map(
      (row) =>
        `<tr data-work-order="${escapeHtml(row.bookingId)}">${cells(
          [row.registration, row.date, row.workOrder, row.reference, row.amount],
          4,
        )}</tr>`,
    )
    .join('');
  const vatBody = document.vatRows
    .map((row) => `<tr>${cells([row.rate, row.net, row.tax, row.gross], 1)}</tr>`)
    .join('');
  const attachments = document.attachments
    .map((attachment) => {
      const body = attachment.lines
        .map(
          (line) =>
            `<tr>${cells([line.description, line.quantity, line.amount], 2)}</tr>`,
        )
        .join('');
      return `<section class="invoice-page attachment" data-page="attachment" data-work-order="${escapeHtml(attachment.bookingId)}">
        <h2>${escapeHtml(document.attachmentHeading)}</h2>
        <h3>${escapeHtml(attachment.title)}</h3>
        <table>
          <thead><tr>${headerCells(
            [
              document.attachmentColumns.description,
              document.attachmentColumns.quantity,
              document.attachmentColumns.amount,
            ],
            2,
          )}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </section>`;
    })
    .join('');
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(document.title)} ${escapeHtml(document.invoiceNumber)}</title>
<style>
  body { font-family: sans-serif; color: #222; margin: 0; }
  h1 { font-size: 20px; margin: 0 0 8px; }
  h2 { font-size: 16px; margin: 0 0 4px; }
  h3 { font-size: 14px; margin: 0 0 12px; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; }
  th, td { border-bottom: 1px solid #ccc; padding: 4px 6px; text-align: left; vertical-align: top; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  .invoice-page { page-break-after: always; }
  .invoice-page:last-child { page-break-after: auto; }
  .attachment { page-break-before: always; }
  .meta { margin: 4px 0; }
</style>
</head>
<body>
<section class="invoice-page" data-page="summary">
  <h1>${escapeHtml(document.title)} ${escapeHtml(document.invoiceNumber)}</h1>
  <p class="meta">${escapeHtml(document.periodLabel)}</p>
  <table>
    <thead><tr>${headerCells(
      [
        document.summaryHeaders.registration,
        document.summaryHeaders.date,
        document.summaryHeaders.workOrder,
        document.summaryHeaders.reference,
        document.summaryHeaders.amount,
      ],
      4,
    )}</tr></thead>
    <tbody>${summaryBody}</tbody>
  </table>
  <h2>${escapeHtml(document.vatTitle)}</h2>
  <table>
    <thead><tr>${headerCells(
      [
        document.vatHeaders.rate,
        document.vatHeaders.net,
        document.vatHeaders.tax,
        document.vatHeaders.gross,
      ],
      1,
    )}</tr></thead>
    <tbody>${vatBody}</tbody>
  </table>
  <p class="meta" data-payment-reference="${escapeHtml(document.paymentReference)}">${escapeHtml(document.paymentReferenceLabel)} ${escapeHtml(document.paymentReference)}</p>
</section>
${attachments}
</body>
</html>`;
}
